import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  type FetchQuestionsResult,
  type QuestionStore,
} from '@/features/jobs/questions/QuestionStore'
import type { AnswersMap } from '@/features/jobs/questions/questions'
import { type Job } from '@/features/jobs/Job'
import { type QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { EMPTY } from 'rxjs'
import {
  catchError,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { action, computed, observable } from 'mobx'
import { task, type Task, TaskGroup } from 'mobx-task'
import { type JobStore } from '@/features/jobs/JobStore'
import { fromStream, tapAction } from '@/utils/mobx-util'
import type { AlertFactory, AlertRetryWrapperFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { withAbortSignal } from '@/utils/rx-util'

/**
 * View model for the dialog that corrects a job's intake answers.
 */
export class CorrectionsDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  /**
   * Questions.
   */
  @observable.ref accessor questions: QuestionListViewModel

  /**
   * Task group for fetching and saving.
   */
  readonly tasks: TaskGroup<unknown[], unknown>

  /**
   * Whether all questions have been answered.
   */
  @observable accessor done = false

  /**
   * Why the answers are being corrected.
   */
  @observable accessor reason = ''

  /**
   * Getter for whether the answers have changed.
   */
  @observable accessor hasChanged = () => false

  constructor(
    public readonly job: Job,
    private readonly _questionStore: QuestionStore,
    private readonly _jobStore: JobStore,
    private readonly _createQuestionListViewModel: () => QuestionListViewModel,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    // Task is invariant in its argument tuple, so tasks with different signatures only group once widened.
    const tasks = [this.fetchQuestions, this.save] as Task<unknown[], unknown>[]
    this.tasks = TaskGroup(tasks)
    this.questions = this._createQuestionListViewModel()
    this._lifecycle.manage(
      this.modal.show$.pipe(
        switchMap(() =>
          this.questions.answersChanged$.pipe(
            // Stored job answers carry no question type and can't be sent
            // back, so the first questions follow from the job's own answers.
            startWith<AnswersMap | null>(null),
            switchMap((answers) =>
              withAbortSignal((signal) =>
                this._createAlertRetryWrapper(
                  'Loading the questions failed',
                  signal,
                )(() => this.fetchQuestions(answers, signal)),
              ).pipe(catchError(() => EMPTY)),
            ),
            takeUntil(this.modal.close$),
          ),
        ),
        tapAction((result: FetchQuestionsResult) => {
          this.done = result.intakeCompleted
          this.questions.reconcile(result.questions)
        }),
      ),
    )
  }

  /**
   * Whether we can save responses.
   */
  @computed
  get canSaveResponses() {
    return this.done && this.hasChanged()
  }

  /**
   * Whether the first page of questions is still loading.
   */
  @computed
  get isLoadingInitial() {
    return (
      this.fetchQuestions.pending && this.questions.questionVms.length === 0
    )
  }

  /**
   * Why saving is disabled, if it is.
   */
  @computed
  get saveTitle() {
    if (!this.hasChanged()) {
      return 'You need to make changes before you can save.'
    }

    return this.done
      ? ''
      : 'You need to answer all questions before you can save.'
  }

  /**
   * The error to show for the last save.
   */
  @computed
  get saveError() {
    return this.save.result === false || this.save.rejected
      ? this.save.error || 'Please check your input and try again.'
      : undefined
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }
    this.fetchQuestions.reset()
    this.done = false
    this.reason = ''
    this.questions = this._createQuestionListViewModel()
    this.hasChanged = fromStream<boolean>(
      false,
      this.questions.answersChanged$.pipe(
        map(() => true),
        take(1),
        takeUntil(this.modal.close$),
      ),
    )
    this.modal.show()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches questions.
   */
  fetchQuestions = task((answers: AnswersMap | null, signal?: AbortSignal) =>
    this._questionStore.fetchQuestions({ jobId: this.job.id, answers, signal }),
  )

  /**
   * Saves the responses.
   */
  save = task.resolved(async (): Promise<boolean> => {
    if (!this.questions.validate()) {
      return false
    }
    if (!this.canSaveResponses) {
      return false
    }

    await this._questionStore
      .makeCorrections(this.job.id, this.questions.answers, this.reason)
      .then(() => this._jobStore.fetchJob(this.job.id))

    this._newAlert()
      .saying('Success!', 'Your changes have been saved.')
      .withState('success')
      .autoDismiss()
      .show()
    this.modal.close()
    return true
  })
}
